"use client";

import { useCallback, useEffect } from "react";
import Link from "next/link";
import { X } from "lucide-react";
import { track } from "../../lib/analytics";
import { type MarketingVideo } from "../../lib/marketingVideos";

// Full-screen video modal for the hero's "Watch the demo" CTA. Charcoal
// scrim, centered 16:9 player, closed by Escape, the backdrop, or the
// close button. The <video> element only mounts while the modal is open,
// so the MP4 costs nothing until the visitor asks for it. Mirrors the
// overlay pattern in Screenshot.tsx (tap-to-enlarge).

interface VideoModalProps {
  video: MarketingVideo;
  open: boolean;
  onClose: () => void;
}

export default function VideoModal({ video, open, onClose }: VideoModalProps) {
  const close = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    // Lock background scroll while the overlay is up.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, close]);

  if (!open || !video.src) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={video.title}
      className="fixed inset-0 z-50 flex items-center justify-center bg-maren-charcoal/90 p-4 sm:p-8"
      onClick={close}
    >
      <button
        type="button"
        onClick={close}
        aria-label="Close video"
        className="absolute top-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-maren-stone/10 text-maren-stone hover:bg-maren-stone/20 transition focus:outline-none focus:ring-2 focus:ring-maren-gold/50"
      >
        <X className="h-5 w-5" aria-hidden />
      </button>
      <div
        className="w-full max-w-5xl"
        onClick={(e) => e.stopPropagation()}
      >
        <video
          className="w-full aspect-video rounded-xl bg-maren-charcoal"
          src={video.src}
          poster={video.poster ?? undefined}
          controls
          autoPlay
          playsInline
        >
          {/* Selectable captions: the native CC toggle appears in the
              player controls when a track is present. Not `default` --
              viewers opt in, YouTube-style. */}
          {video.captions && (
            <track
              kind="captions"
              srcLang="en"
              label="English"
              src={video.captions}
            />
          )}
        </video>
        {/* The most-persuaded visitor on the site is the one who just
            finished the demo; never leave them at a dead end. */}
        <div className="mt-4 flex items-center justify-center gap-4">
          <Link
            href="/signup"
            onClick={() => track({ name: "cta_click", props: { location: "demo_modal" } })}
            className="inline-flex items-center justify-center rounded-lg bg-maren-green hover:bg-maren-green/90 active:bg-maren-green/80 px-6 py-3 text-sm font-semibold text-maren-stone shadow-sm transition focus:outline-none focus:ring-2 focus:ring-maren-gold/50"
          >
            Start Free
          </Link>
          <span className="text-[13px] text-maren-stone/60">No card required.</span>
        </div>
      </div>
    </div>
  );
}
